import { useEffect, useState } from 'react'
import type { ShareMode, TextKey } from '@praesto/shared'
import { api, ApiError } from '../api.js'
import { navigate } from '../router.js'
import { Brandmark } from '../components/Brandmark.js'
import { LanguageSwitch } from '../components/LanguageSwitch.js'
import { useText } from '../i18n.js'

/** Der zuletzt verwendete Name, damit man ihn nicht jedes Mal neu tippt. */
const NAME_KEY = 'praesto.guest.name'

interface LinkInfo {
  boardId: string
  boardTitle: string
  mode: ShareMode
  joinedAs: string | null
}

/**
 * Was der Gast über seinen Bewegungsspielraum wissen muss.
 *
 * Bewusst kürzer gefasst als die Beschreibungen im Freigabe-Dialog: Dort wählt
 * jemand einen Modus aus und vergleicht, hier steht schon fest, welcher gilt.
 */
const HINT: Record<ShareMode, TextKey> = {
  explore: 'guest.hint.explore',
  guided: 'guest.hint.guided',
  locked: 'guest.hint.locked',
}

/**
 * Einstieg für Gäste.
 *
 * Der Name wird vor dem Eintreten abgefragt — nicht aus Formalität, sondern
 * damit der Vortragende in der Teilnehmerliste sieht, wer im Raum ist. Er
 * landet in einer eigenen Gast-Sitzung; jeder Teilnehmer bekommt dadurch eine
 * eigene Identität und Cursorfarbe, auch wenn alle denselben Link benutzen.
 */
export function GuestPage({ token }: { token: string }): React.ReactElement {
  const t = useText()
  const [info, setInfo] = useState<LinkInfo | null>(null)
  const [name, setName] = useState(() => localStorage.getItem(NAME_KEY) ?? '')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    api
      .guestLinkInfo(token)
      .then((data) => {
        setInfo(data)
        // Wer diesem Link in diesem Browser schon beigetreten ist, wird nicht
        // erneut gefragt — ein Neuladen soll niemanden aufhalten.
        if (data.joinedAs) navigate(`/b/${data.boardId}`, true)
        else if (!localStorage.getItem(NAME_KEY)) setName('')
      })
      .catch((caught) =>
        setError(caught instanceof ApiError ? caught.message : t('guest.linkFailed')),
      )
  }, [token, t])

  async function join(event: React.FormEvent): Promise<void> {
    event.preventDefault()
    const trimmed = name.trim()
    if (!trimmed) {
      setError(t('guest.nameRequired'))
      return
    }
    setBusy(true)
    setError(null)
    try {
      const result = await api.joinAsGuest(token, trimmed)
      localStorage.setItem(NAME_KEY, trimmed)
      navigate(`/b/${result.boardId}`, true)
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : t('guest.joinFailed'))
      setBusy(false)
    }
  }

  if (error && !info) {
    return (
      <div className="auth-screen brand-surface">
        <div className="auth-card">
          <Brandmark />
          <p className="form-error">{error}</p>
        </div>
      </div>
    )
  }

  if (!info || info.joinedAs) {
    return (
      <div className="auth-screen brand-surface">
        <p className="muted">{t('guest.opening')}</p>
      </div>
    )
  }

  return (
    <div className="auth-screen brand-surface">
      <form className="auth-card" onSubmit={join}>
        <Brandmark />
        <p className="join-board">{info.boardTitle}</p>
        <p className="auth-lead">{t(HINT[info.mode])}</p>

        <label className="field">
          <span>{t('guest.yourName')}</span>
          <input
            value={name}
            onChange={(event) => {
              setName(event.target.value)
              setError(null)
            }}
            placeholder={t('guest.namePlaceholder')}
            maxLength={60}
            autoFocus
            autoComplete="name"
          />
        </label>

        {error && <p className="form-error">{error}</p>}

        <button className="button primary" type="submit" disabled={busy}>
          {busy ? t('common.moment') : t('guest.enter')}
        </button>

        {/*
          Auch hier, und nicht nur im Anmeldebildschirm: Ein Gast hat kein Konto,
          in dem eine Sprache stünde — dieser Bildschirm ist die einzige Stelle,
          an der er die Wahl überhaupt treffen kann, bevor das Board aufgeht.
        */}
        <LanguageSwitch />
      </form>
    </div>
  )
}
